import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
    INotifyReferralReceiveRewardData,
    IQueryParamsReferralsPayload, IReferrerRewardBalance, IResponseReferralReceivedReward,
    ReferralInvitedFriend,
    ResponsePaginationReferralInvitedFriends
} from "./referral.t";
import { ValueOf } from "../../types";
import { REFERRAL_DISPLAY_FOR_TYPE } from "./referralConstants";

interface IReferralQRCodeState {
    isLoading: boolean;
    data: any;
    error: string | null;
}

interface IShareReferralLInkState {
    error: string | null;
}

interface IQueryParamsReferrals {
    currentPage: number;
    totalPages: number;
}

interface IWarningResetProgress {
    accountCode: string;
    email: string;
}

interface IRespawnTimer {
    displayForType: ValueOf<typeof REFERRAL_DISPLAY_FOR_TYPE>;
    endDateTimeRespawn: string
}

export interface IReferralState {
    errorGetRefCode: string | null;
    errorGetReferrals: string | null;
    errorReferralReward: string | null;
    errorReferralReceive: string | null;
    errorReferral: string | null;
    isLoadingReferral: boolean;
    isLoadingReferrals: boolean;
    isLoadingReferralReceive: boolean;
    isPortfolioBannerOpened: boolean;
    qrCode: IReferralQRCodeState;
    queryParamsReferrals: IQueryParamsReferrals;
    refCode: string;
    referral: ReferralInvitedFriend | null
    referralLink: string;
    referrals: ReferralInvitedFriend[];
    referrerReward: IReferrerRewardBalance | null;
    referrerGiftRewardValue: number;
    shareLink: IShareReferralLInkState;
    notifyReceivedReward: INotifyReferralReceiveRewardData | null;
    warningResetProgress: IWarningResetProgress | null;
    respawnTimer: IRespawnTimer | null
}

const initialState: IReferralState = {
    errorGetReferrals: null,
    errorGetRefCode: null,
    errorReferral: null,
    errorReferralReward: null,
    errorReferralReceive: null,
    isLoadingReferral: true,
    isLoadingReferrals: true,
    isLoadingReferralReceive: true,
    isPortfolioBannerOpened: true,
    qrCode: {
        isLoading: false,
        data: null,
        error: null,
    },
    queryParamsReferrals: { currentPage: 0, totalPages: 0 },
    refCode: '',
    referral: null,
    referrals: [],
    referralLink: '',
    referrerReward: {
        currentRewards: 0,
        totalReceived: 0,
    },
    referrerGiftRewardValue: 0,
    shareLink: {
        error: null,
    },
    notifyReceivedReward: null,
    warningResetProgress: null,
    respawnTimer: null,
};

export const referralSlice = createSlice({
    name: 'referral',
    initialState,
    reducers: {
        getReferralCodeAction: () => {
        },
        getReferralCodeSuccess: (state, action: PayloadAction<string>) => {
            state.refCode = action.payload;
        },
        getReferralCodeError: (state, action) => {
            state.errorGetRefCode = action.payload;
        },
        getReferralQRCodeAction: () => {
        },
        getReferralQRCodeStart: (state) => {
            state.qrCode.isLoading = true;
            state.qrCode.error = null;
        },
        getReferralQRCodeSuccess: (state, action: PayloadAction<string>) => {
            state.qrCode.isLoading = false;
            state.qrCode.data = action.payload;
        },
        getReferralQRCodeError: (state, action: PayloadAction<string>) => {
            state.qrCode.isLoading = false;
            state.qrCode.error = action.payload;
        },
        getReferralInvitedFriendsAction: (state, action: PayloadAction<IQueryParamsReferralsPayload>) => {
            state.isLoadingReferrals = true;
            state.errorGetReferrals = null;
        },
        getReferralInvitedFriendsSuccess: (state, action: PayloadAction<ResponsePaginationReferralInvitedFriends>) => {
            const { items, currentPage, totalPages } = action.payload;

            state.referrals = [...state.referrals, ...items];
            state.queryParamsReferrals = {
                currentPage,
                totalPages
            }
            state.isLoadingReferrals = false;
        },
        getReferralInvitedFriendsError: (state, action) => {
            state.errorGetReferrals = action.payload;
        },
        getReferralLinkSuccess: (state, action: PayloadAction<string>) => {
            state.referralLink = action.payload;
        },
        getReferralLinkError: (state, action: PayloadAction<string>) => {
            state.shareLink.error = action.payload;
        },
        getReferralAction: (state) => {
            state.isLoadingReferral = true;
            state.errorReferral = null;
        },
        getReferralSuccess: (state, action: PayloadAction<ReferralInvitedFriend>) => {
            state.referral = action.payload;
            state.isLoadingReferral = false;
        },
        getReferralError: (state, action: PayloadAction<string>) => {
            state.errorReferral = action.payload;
        },
        getReferrerRewardBalanceAction: (state) => {
            state.isLoadingReferralReceive = true;
            state.errorReferralReward = null;
        },
        getReferrerRewardBalanceSuccess: (state, action: PayloadAction<IReferrerRewardBalance>) => {
            state.referrerReward = action.payload;
            state.isLoadingReferralReceive = false;
        },
        getReferrerRewardBalanceError: (state, action: PayloadAction<string>) => {
            state.errorReferralReward = action.payload;
        },
        addFundsToReferrerAction: () => {
        },
        addFundsToReferrerSuccess: (state, action: PayloadAction<IResponseReferralReceivedReward>) => {
            const {totalReceived, received, currentRewards} = action.payload;
            state.referrerGiftRewardValue = received;
            state.referrerReward = {
                totalReceived,
                currentRewards
            }
        },
        addFundsToReferrerError: (state, action: PayloadAction<string>) => {
            state.errorReferralReceive = action.payload;
        },
        setReferralNotifyReceivedReward: (state, action) => {
            state.notifyReceivedReward = action.payload;
        },
        setRespawnTimer: (state, action: PayloadAction<IRespawnTimer>) => {
            state.respawnTimer = action.payload;
        },
        setWarningResetProgress: (state, action: PayloadAction<IWarningResetProgress>) => {
            state.warningResetProgress = action.payload;
        }
    },
});

export default referralSlice.reducer;

